import { EXCHANGE_RATE_FALLBACK } from '~/config.js';

const CACHE_KEY = 'exchangeRateCache';

// A remembered rate older than this says nothing about today's price, so the
// hardcoded fallback takes over:
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const normalize = currency => String(currency || '').toLowerCase();

const readCache = function readRememberedRates() {
    try {
        return JSON.parse(window.localStorage.getItem(CACHE_KEY)) || {};
    } catch (e) {
        return {};
    }
};

/**
 * Keeps the last rate the api actually returned, so an outage — or just the
 * moment before the request lands — doesn't leave the page without fiat.
 *
 * @param  {String} currency
 * @param  {Number} rate
 * @param  {Number} trend
 * @return {undefined}
 */
export const rememberExchangeRate = function rememberLastKnownRate(currency, rate, trend = 0) {
    if (!(rate > 0)) {
        return;
    }

    try {
        const cache = readCache();
        cache[normalize(currency)] = { rate, trend, ts: Date.now() };
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
        // No storage (private mode, quota): the hardcoded fallback still works.
    }
};

/**
 * What to show while the rate is loading and when it never arrives: the last
 * rate seen in this browser, and failing that the hardcoded one from config.
 * Zero means there is neither — a currency without a fallback — and fiat
 * values stay hidden, exactly as they do today.
 *
 * @param  {String} currency
 * @return {Array[Number, Number]}  [rate, trend]
 */
export const getFallbackExchangeRate = function getRateToShowWhenApiIsDown(currency) {
    const key = normalize(currency);
    const cached = readCache()[key];

    if (cached?.rate > 0 && Date.now() - (cached.ts || 0) < CACHE_TTL_MS) {
        return [cached.rate, cached.trend || 0];
    }

    return [EXCHANGE_RATE_FALLBACK[key] || 0, 0];
};
